import { SportslinePickSnippetCardProps } from '@components/sportsline-pick-snippet-card/sportsline-pick-snippet-card';
import { Component, Host, Prop, h } from '@stencil/core';
import { formaPayout, formatOdds, formatWager, calculatePayout } from '@utils/bettingUtils';
import { getBookAssetsById } from '@utils/books';
import { capitalizeFirstLetter } from '@utils/stringUtils';

// https://www.figma.com/design/TLXf8t3qC5vIMbEC9T3ODH/Bettable-Odds?node-id=2627-31369&p=f&m=dev

type ParlayInfoProps = {
  label: string;
  value: string | number;
};

const ParlayInfo = (props: ParlayInfoProps) => [
  <div
    class={{
      'sportsline-pick-snippet-parlay-card__info': true,
    }}
  >
    {props.label} <strong>{props.value}</strong>
  </div>,
];

const Leg = (
  props: SportslinePickSnippetParlayCardProps['legs'][0] & {
    isBranded?: boolean;
  },
) => {
  const isPlayerProp = props.type === 'player';
  const isTeamProp = props.type === 'team';
  const isYesNoMarket = ['yes', 'no'].includes(props.side?.toLowerCase());

  const getSubLabel = () => {
    const marketName = capitalizeFirstLetter(props.marketLabel?.toLowerCase() || '');
    if (isPlayerProp) {
      if (isYesNoMarket) {
        return [props.marketLabel];
      }
      return [props.playerName, marketName];
    }
    //
    if (isTeamProp) {
      return [props.teamName, marketName];
    }
    //
    return [marketName];
  };

  const getLabel = () => {
    if (isPlayerProp) {
      if (isYesNoMarket) {
        return props.playerName;
      }
    }
    return props.label;
  };

  const getSelectionAsset = () => {
    const side = props.side?.toUpperCase() || null;
    const type = props.type?.toLowerCase() || 'standard';
    const assetSize = props.assetSize || 'medium';

    switch (type) {
      case 'player':
        return (
          <sportsline-player-avatar
            style={{
              '--sportsline-player-avatar-background-color': props.activeColor,
            }}
            playerId={(props.playerId ?? -1).toString()}
            size={assetSize}
          />
        );
      case 'team':
        return <sportsline-team-logo variant="dark" teamId={props.teamId.toString()} size={assetSize} />;

      default: {
        if (['OVER', 'UNDER'].includes(side)) {
          return <sportsline-over-under-image size={assetSize} selection={props.side} />;
        }

        if (Boolean(props.teamId)) {
          return <sportsline-team-logo variant="dark" teamId={props.teamId.toString()} size={assetSize} />;
        }

        return null;
      }
    }
  };

  return [
    <div
      class={{
        'sportsline-pick-snippet-parlay-card__leg': true,
      }}
      style={{
        '--sportsline-pick-snippet-parlay-card-color': props.activeColor,
      }}
    >
      <div
        class={{
          'sportsline-pick-snippet-parlay-card__leg-color': true,
        }}
      />
      <div
        class={{
          'sportsline-pick-snippet-parlay-card__leg-selection': true,
        }}
      >
        {!props.isBranded && getSelectionAsset()}
        <div
          class={{
            'sportsline-pick-snippet-parlay-card__leg-selection__info': true,
          }}
        >
          <div
            class={{
              'sportsline-pick-snippet-parlay-card__leg-selection__label': true,
            }}
          >
            {getLabel()}
          </div>
          <div
            class={{
              'sportsline-pick-snippet-parlay-card__market': true,
            }}
          >
            {getSubLabel()
              .filter(Boolean)
              .map(v => (
                <span key={v}>{v}</span>
              ))}
          </div>
          <div
            class={{
              'sportsline-pick-snippet-parlay-card__leg-selection__metadata': true,
            }}
          >
            {props.gameInfoLabel}
          </div>
        </div>
        <div
          class={{
            'sportsline-pick-snippet-parlay-card__leg-selection__odds': true,
          }}
        >
          {formatOdds(props.odds)}
        </div>
      </div>
    </div>,
  ];
};

export type SportslinePickSnippetParlayCardProps = {
  legs?: Array<SportslinePickSnippetCardProps>;
  odds?: string | number; // Odds for the parlay, can be a string or a number
  wager?: number; // Wager amount for the parlay, can be a string or a number, defaults to 10
};

@Component({
  tag: 'sportsline-pick-snippet-parlay-card',
  styleUrl: 'sportsline-pick-snippet-parlay-card.css',
  shadow: true,
})
export class SportslinePickSnippetParlayCard {
  /**
   * isBranded
   */
  @Prop() isBranded: boolean = false;

  /**
   * Book ID, e.g., "35"
   */
  @Prop() bookId?: string | number | null;

  /**
   * Book Name, e.g., "FanDuel"
   */
  @Prop() bookName?: string | null;

  /**
   * Slogan for the book.
   */
  @Prop() bookSlogan?: string | null;

  /**
   * Legs in the parlay.
   *
   */
  @Prop() legs: SportslinePickSnippetParlayCardProps['legs'] = [];

  /**
   * Odds for the parlay.
   * This can be a string or a number.
   * If not provided, it defaults to null.
   */
  @Prop() odds?: SportslinePickSnippetParlayCardProps['odds'] = null;

  /**
   * Wager amount for the parlay.
   * This can be a string or a number.
   * If not provided, it defaults to 10.
   */
  @Prop() wager?: SportslinePickSnippetParlayCardProps['wager'] = 10;

  private get getNumberOfLegsLabel() {
    const nLegs = this.legs.length;
    if (nLegs === 0) {
      return 'No Legs';
    }
    return `${nLegs}-Leg Parlay`;
  }

  private get getOdds() {
    return this.odds ?? 0;
  }

  private get getWager() {
    return parseFloat(`${this.wager ?? '10'}`); // Default to 10 if wager is not provided
  }

  render() {
    const wager = this.getWager;
    const odds = this.getOdds;
    const payout = calculatePayout(odds, wager);
    const isBranded = Boolean(this.isBranded);

    return (
      <Host>
        <div
          class={{
            'sportsline-pick-snippet-parlay-card': true,
            'sportsline-pick-snippet-parlay-card--branded': isBranded,
            [`sportsline-pick-snippet-parlay-card--branded--${this.bookId}`]: isBranded, // Example for branded variant
          }}
        >
          {isBranded && (
            <div
              class={{
                'sportsline-pick-snippet-parlay-card__header-brand': true,
              }}
            >
              <div
                class={{
                  'sportsline-pick-snippet-parlay-card__header-brand__icon': true,
                }}
              >
                <sportsline-image props={{ src: getBookAssetsById(`${this.bookId}`)?.icon, alt: this.bookName }} />
              </div>
              <div
                class={{
                  'sportsline-pick-snippet-parlay-card__header-brand__logo': true,
                }}
              >
                <sportsline-image props={{ src: getBookAssetsById(`${this.bookId}`)?.logo, alt: this.bookName }} />
              </div>
              {this.bookSlogan && (
                <div
                  class={{
                    'sportsline-pick-snippet-parlay-card__header-brand__slogan': true,
                  }}
                >
                  {this.bookSlogan}
                </div>
              )}
            </div>
          )}
          <div
            class={{
              'sportsline-pick-snippet-parlay-card__header': true,
            }}
          >
            <div
              class={{
                'sportsline-pick-snippet-parlay-card-label': true,
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none">
                <path
                  d="M4.32227 2.88767C4.4907 2.9719 4.65083 3.08972 4.79395 3.22459C4.937 3.35942 5.04663 3.5279 5.13086 3.69627C5.17299 3.79764 5.21587 3.89894 5.24121 3.99998C5.28333 4.15169 5.2998 4.31213 5.2998 4.46385C5.29976 4.64081 5.27112 4.8175 5.21777 4.98728L5.15625 5.15428C5.09734 5.28918 5.0215 5.41506 4.92871 5.53318C4.88659 5.59208 4.83681 5.65145 4.78613 5.70213L3 7.48826C2.30917 8.17085 1.19629 8.17085 0.513672 7.48826C0.168256 7.15136 0.000105416 6.69642 0 6.24998C0 5.79497 0.168361 5.34843 0.513672 5.00291L2.29199 3.22459C2.35067 3.17442 2.40129 3.12402 2.45996 3.08201C2.36761 3.46946 2.3926 3.87378 2.52734 4.24412L1.1377 5.63474C0.800788 5.97173 0.800822 6.52723 1.1377 6.86424C1.30621 7.03275 1.53372 7.11717 1.75293 7.11717C1.97205 7.1171 2.19972 7.03268 2.36816 6.86424L4.1709 5.09471C4.35596 4.90946 4.43963 4.66493 4.42285 4.42088C4.41462 4.31136 4.38975 4.21033 4.34766 4.10935C4.30555 4.0166 4.24653 3.93208 4.1709 3.85642C4.09528 3.78047 4.01068 3.72178 3.91797 3.67967C3.87586 3.49445 3.9263 3.28377 4.06934 3.1406L4.32227 2.88767ZM5.00488 0.511698C5.69564 -0.170582 6.80766 -0.170549 7.49023 0.511698C7.79266 0.806619 7.96011 1.19208 7.99707 1.58299L8.00488 1.75096C8.0048 2.2057 7.83574 2.65254 7.49023 2.99803L5.7041 4.78416C5.64543 4.83433 5.5948 4.88473 5.53613 4.92674C5.62854 4.5391 5.60269 4.13415 5.46777 3.76365L6.86719 2.36619C7.20422 2.02917 7.20422 1.47274 6.86719 1.13572C6.69868 0.967273 6.47112 0.882791 6.25195 0.882791C6.03283 0.882856 5.80517 0.967277 5.63672 1.13572L3.83398 2.90525C3.6488 3.09051 3.56429 3.33496 3.58105 3.57908C3.58928 3.68865 3.61512 3.7896 3.65723 3.8906C3.69934 3.98339 3.75832 4.06785 3.83398 4.14353C3.90965 4.21954 3.99413 4.27817 4.08691 4.32029C4.12901 4.50542 4.07843 4.7162 3.93555 4.85935L3.68262 5.11228C3.5141 5.02803 3.35313 4.91853 3.20996 4.77537C3.06693 4.64048 2.95725 4.47208 2.87305 4.30369C2.83092 4.20231 2.78901 4.10102 2.76367 3.99998C2.7216 3.84843 2.70511 3.68865 2.70508 3.53709C2.70508 3.30111 2.75484 3.06488 2.84766 2.84568C2.90654 2.71081 2.98244 2.58455 3.0752 2.46678C3.11732 2.40787 3.16806 2.34851 3.21875 2.29783L5.00488 0.511698Z"
                  fill-opacity="0.7"
                />
              </svg>
              {this.getNumberOfLegsLabel}
            </div>
          </div>
          <div
            class={{
              'sportsline-pick-snippet-parlay-card__legs': true,
            }}
          >
            {this.legs.map(leg => (
              <Leg {...leg} isBranded={this.isBranded} />
            ))}
          </div>
          <div
            class={{
              'sportsline-pick-snippet-parlay-card__footer': true,
            }}
          >
            <div
              class={{
                'sportsline-pick-snippet-parlay-card__footer__inner-wrapper': true,
              }}
            >
              <div
                class={{
                  'sportsline-pick-snippet-parlay-card__footer-content': true,
                }}
              >
                <ParlayInfo label="Wager" value={formatWager(wager)} />
                <ParlayInfo label="Payout" value={formaPayout(payout)} />
                <ParlayInfo label="Odds" value={formatOdds(odds)} />
              </div>
              <div
                class={{
                  'sportsline-pick-snippet-parlay-card__action': true,
                }}
              >
                <slot></slot>
              </div>
            </div>
          </div>
          <div
            class={{
              'sportsline-pick-snippet-parlay-card__disclaimer': true,
            }}
          >
            <slot name="disclaimer"></slot>
          </div>
        </div>
      </Host>
    );
  }
}
